/* AdminAfter10thSection.jsx — Admin preview of the After-10th Group Quizzes.
 *
 * Displays the 5 group quizzes (MPC, BiPC, MEC, CEC, HEC). Click a group →
 * inline drawer with ONE QUESTION AT A TIME (matching the student
 * experience), plus admin-only annotations:
 *   - per-option fit_score badge
 *   - Q16 correct-option highlighted
 *   - scoring bands + result_report preview at the end
 *
 * Read-only. Admin cannot mutate content from this UI (content lives in
 * /app/backend/content/after10th_groups.json).
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  GraduationCap, ChevronRight, ArrowRight, ArrowLeft, X, Loader2,
  CheckCircle2, Atom, FlaskConical, LineChart, Scale, BookOpen,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GROUP_VISUALS = {
  MPC:  { Icon: Atom,         gradient: 'from-blue-500 to-indigo-600',     chip: 'bg-blue-100 text-blue-800' },
  BiPC: { Icon: FlaskConical, gradient: 'from-emerald-500 to-green-600',   chip: 'bg-emerald-100 text-emerald-800' },
  MEC:  { Icon: LineChart,    gradient: 'from-violet-500 to-fuchsia-600',  chip: 'bg-violet-100 text-violet-800' },
  CEC:  { Icon: Scale,        gradient: 'from-amber-500 to-orange-600',    chip: 'bg-amber-100 text-amber-900' },
  HEC:  { Icon: BookOpen,     gradient: 'from-rose-500 to-pink-600',       chip: 'bg-rose-100 text-rose-800' },
};

export default function AdminAfter10thSection() {
  const [groups, setGroups] = useState(null);
  const [openGroup, setOpenGroup] = useState(null); // 'MPC' | 'BiPC' | …

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/after-10th/groups`);
        if (!cancelled) setGroups(data.groups);
      } catch (e) {
        if (!cancelled) setGroups([]);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (!groups) {
    return <div className="text-sm text-gray-500"><Loader2 className="inline w-4 h-4 animate-spin mr-2"/> Loading After-10th quizzes…</div>;
  }

  return (
    <div data-testid="admin-after10th-section">
      <p className="text-sm text-gray-600 mb-5">
        Five Intermediate group quizzes loaded from <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">after10th_groups.json</code>.
        Click a group to preview every question one-at-a-time — exactly how a student sees it,
        plus per-option <span className="font-semibold">fit_score</span> annotations.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="admin-after10th-grid">
        {groups.map((g) => {
          const v = GROUP_VISUALS[g.group] || GROUP_VISUALS.MPC;
          const Icon = v.Icon;
          return (
            <button
              key={g.group}
              type="button"
              onClick={() => setOpenGroup(g.group)}
              data-testid={`admin-after10th-group-${g.group}`}
              className="text-left bg-white rounded-2xl border border-gray-200 hover:border-gray-300 hover:shadow-md p-4 transition-all group"
            >
              <div className="flex items-center justify-between mb-2.5">
                <span className={`inline-flex w-10 h-10 rounded-xl bg-gradient-to-br ${v.gradient} items-center justify-center text-white shadow-sm group-hover:scale-110 transition-transform`}>
                  <Icon size={18}/>
                </span>
                <span className={`inline-flex px-2 py-0.5 rounded-full ${v.chip} text-[10px] font-extrabold uppercase tracking-widest`}>
                  18 Qs · 4 areas
                </span>
              </div>
              <h3 className="text-xl font-extrabold text-gray-900 leading-none">{g.group}</h3>
              <p className="text-xs font-semibold text-gray-600 mt-1">{g.group_full_form}</p>
              <p className="text-xs text-gray-500 mt-2.5 line-clamp-2">{g.purpose}</p>
              <span className="inline-flex items-center gap-1 mt-3 text-xs font-bold text-gray-700 group-hover:text-gray-900">
                Preview <ChevronRight size={12}/>
              </span>
            </button>
          );
        })}
      </div>

      {openGroup && (
        <AdminQuizDrawer group={openGroup} onClose={() => setOpenGroup(null)}/>
      )}
    </div>
  );
}

/* ── One-question-at-a-time inline drawer ──────────────────────────────── */
function AdminQuizDrawer({ group, onClose }) {
  const [quiz, setQuiz] = useState(null);
  const [step, setStep] = useState(0);  // 0..(total-1) for questions, or 'report' at end
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/after-10th/admin/quiz/${group}`, { withCredentials: true });
        if (!cancelled) setQuiz(data);
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.detail || 'Could not load the quiz.');
      }
    })();
    return () => { cancelled = true; };
  }, [group]);

  const flat = quiz ? quiz.sections.flatMap((s, secIdx) => s.questions.map((q) => ({ ...q, area: s.area, secIdx }))) : [];
  const total = flat.length;
  const visualKey = group.toUpperCase() in GROUP_VISUALS ? group.toUpperCase() : (Object.keys(GROUP_VISUALS).find((k) => k.toUpperCase() === group.toUpperCase()) || 'MPC');
  const palette = GROUP_VISUALS[visualKey];

  return (
    <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center p-0 md:p-6 bg-black/40 backdrop-blur-sm" data-testid="admin-after10th-drawer">
      <div className="bg-white w-full md:max-w-3xl max-h-[92vh] overflow-y-auto md:rounded-3xl shadow-2xl">
        {/* Drawer header */}
        <div className={`sticky top-0 z-10 bg-gradient-to-r ${palette.gradient} text-white px-5 py-3 md:rounded-t-3xl flex items-center justify-between`}>
          <div>
            <p className="text-[10px] font-extrabold uppercase tracking-widest text-white/80">Admin preview</p>
            <h3 className="text-base font-extrabold" data-testid="admin-drawer-title">{group} — {quiz?.group_full_form || '…'}</h3>
          </div>
          <button type="button" onClick={onClose} className="w-8 h-8 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center" aria-label="Close" data-testid="admin-drawer-close">
            <X size={16}/>
          </button>
        </div>

        <div className="p-5 md:p-6">
          {error && <p className="text-sm text-red-600">{error}</p>}
          {!quiz && !error && (
            <div className="py-10 text-center text-gray-500"><Loader2 className="inline w-5 h-5 animate-spin mr-2"/> Loading…</div>
          )}
          {quiz && step === 'report' && <AdminReportPreview quiz={quiz}/>}
          {quiz && step !== 'report' && (
            <>
              {/* progress indicator */}
              <div className="flex items-center gap-3 mb-5">
                <span className="text-xs font-extrabold text-gray-700 whitespace-nowrap" data-testid="admin-step-indicator">
                  Question {step + 1} <span className="text-gray-400">of {total}</span>
                </span>
                <div className="flex-1 h-1.5 rounded-full bg-gray-200 overflow-hidden">
                  <div className={`h-full bg-gradient-to-r ${palette.gradient} transition-all`} style={{ width: `${Math.round(((step + 1) / total) * 100)}%` }}/>
                </div>
              </div>
              <AdminQuestionCard q={flat[step]} palette={palette}/>

              <div className="mt-6 flex items-center justify-between">
                <button type="button" onClick={() => setStep((s) => Math.max(0, s - 1))} disabled={step === 0}
                        className={`inline-flex items-center gap-1.5 text-sm font-semibold ${step === 0 ? 'text-gray-300 cursor-not-allowed' : 'text-gray-600 hover:text-gray-900'}`} data-testid="admin-prev-btn">
                  <ArrowLeft size={14}/> Previous
                </button>
                <span className="text-xs text-gray-400">Area {flat[step].secIdx + 1} · {flat[step].area}</span>
                {step === total - 1 ? (
                  <button type="button" onClick={() => setStep('report')}
                          className={`inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r ${palette.gradient} text-white text-sm font-bold shadow hover:shadow-lg`}
                          data-testid="admin-show-report-btn">
                    Show report <ArrowRight size={14}/>
                  </button>
                ) : (
                  <button type="button" onClick={() => setStep((s) => Math.min(total - 1, s + 1))}
                          className={`inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r ${palette.gradient} text-white text-sm font-bold shadow hover:shadow-lg`}
                          data-testid="admin-next-btn">
                    Next <ArrowRight size={14}/>
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function AdminQuestionCard({ q, palette }) {
  return (
    <div className="border border-gray-200 rounded-2xl overflow-hidden">
      <div className={`h-1.5 bg-gradient-to-r ${palette.gradient}`}/>
      <div className="p-5">
        <p className="text-xs font-bold text-gray-500 uppercase tracking-widest">{q.id}{q.knowledge_question && ' · Knowledge check'}</p>
        <p className="text-base md:text-lg font-bold text-gray-900 mt-2 leading-snug" data-testid={`admin-q-${q.id}-text`}>{q.question}</p>

        <div className="mt-4 grid grid-cols-1 gap-2">
          {q.options.map((opt) => {
            const isCorrect = opt.correct === true;
            return (
              <div key={opt.key}
                   className={`flex items-start gap-3 px-4 py-3 rounded-xl border ${isCorrect ? 'border-emerald-300 bg-emerald-50' : 'border-gray-200 bg-gray-50'}`}
                   data-testid={`admin-opt-${q.id}-${opt.key}`}>
                <span className={`shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-xs font-extrabold ${isCorrect ? 'bg-emerald-200 text-emerald-900' : 'bg-white text-gray-600 border border-gray-200'}`}>{opt.key}</span>
                <span className="flex-1 text-sm text-gray-800 leading-relaxed">{opt.text}</span>
                <span className={`shrink-0 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-extrabold uppercase tracking-widest ${opt.fit_score >= 2 ? 'bg-emerald-100 text-emerald-800' : opt.fit_score === 1 ? 'bg-amber-100 text-amber-900' : 'bg-gray-200 text-gray-600'}`}>
                  fit {opt.fit_score}
                </span>
                {isCorrect && <CheckCircle2 size={14} className="text-emerald-600 shrink-0 mt-0.5"/>}
              </div>
            );
          })}
        </div>

        {q.knowledge_question && q.explanation && (
          <div className="mt-4 p-3 rounded-xl bg-amber-50 border border-amber-100">
            <p className="text-[10px] font-extrabold uppercase tracking-widest text-amber-700 mb-1">Explanation</p>
            <p className="text-xs text-amber-900 leading-relaxed">{q.explanation}</p>
          </div>
        )}
      </div>
    </div>
  );
}

function AdminReportPreview({ quiz }) {
  const sc = quiz.scoring;
  const rep = quiz.result_report;
  return (
    <div className="space-y-5" data-testid="admin-report-preview">
      <div>
        <p className="text-[10px] font-extrabold uppercase tracking-widest text-gray-500 mb-2">Scoring</p>
        <p className="text-sm text-gray-700 mb-3">{sc.method}</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {sc.bands.map((b) => (
            <div key={b.band} className="rounded-xl border border-gray-200 p-3">
              <p className="text-xs font-extrabold text-gray-900">{b.band} <span className="text-gray-400 font-bold">· {b.range}</span></p>
              <p className="text-xs text-gray-600 mt-1 leading-relaxed">{b.meaning}</p>
            </div>
          ))}
        </div>
      </div>

      <div>
        <p className="text-[10px] font-extrabold uppercase tracking-widest text-gray-500 mb-2">Always shown to the student</p>
        <div className="rounded-xl border border-gray-200 p-4 space-y-3 text-sm text-gray-700">
          <p><span className="font-bold">Group:</span> {rep.always_show.group}</p>
          <p><span className="font-bold">What you study:</span> {rep.always_show.what_you_study}</p>
          <p><span className="font-bold">Where it leads:</span> {rep.always_show.where_it_leads}</p>
          <p><span className="font-bold">Does not lead to:</span> {rep.always_show.does_not_lead_to}</p>
          <p><span className="font-bold">AI angle:</span> {rep.always_show.ai_angle}</p>
          <p className="text-amber-900 bg-amber-50 border border-amber-100 rounded-lg p-2"><span className="font-bold">Honest note:</span> {rep.always_show.honest_note}</p>
        </div>
      </div>

      <div>
        <p className="text-[10px] font-extrabold uppercase tracking-widest text-gray-500 mb-2">Band-specific messages</p>
        <div className="grid grid-cols-1 gap-2">
          {Object.entries(rep.by_band).map(([band, msg]) => (
            <div key={band} className="rounded-xl border border-gray-200 p-3">
              <p className="text-xs font-extrabold text-gray-900">{band}</p>
              <p className="text-xs text-gray-700 mt-1 leading-relaxed">{msg}</p>
            </div>
          ))}
        </div>
      </div>

      {quiz.ai_awareness_note && (
        <div>
          <p className="text-[10px] font-extrabold uppercase tracking-widest text-gray-500 mb-2">AI awareness note</p>
          <p className="text-sm text-gray-700 leading-relaxed bg-gray-50 border border-gray-200 rounded-xl p-3">{quiz.ai_awareness_note}</p>
        </div>
      )}
    </div>
  );
}
